import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/ui/status-badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, Shield, Lock, Languages } from "lucide-react";
import { useIsMobile } from '@/hooks/use-mobile';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { useBadges } from '@/hooks/useBadges';
import { useCommunity } from '@/contexts/CommunityContext';
import { Badge as BadgeType } from '@communitly/shared';
import { BadgeForm } from '@/components/admin/BadgeForm';
import { BadgeTranslationManagement } from '@/components/admin/BadgeTranslationManagement';
import { getIconComponent } from '@/utils/iconUtils';

const BadgeManagement = () => {
  const { currentCommunity } = useCommunity();
  const { data: badges, isLoading: badgesLoading } = useBadges(currentCommunity?.id || '');
  const isMobile = useIsMobile();
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingBadge, setEditingBadge] = useState<BadgeType | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [badgeToDelete, setBadgeToDelete] = useState<string>('');
  const [isTranslationManagementOpen, setIsTranslationManagementOpen] = useState(false);

  const queryClient = useQueryClient();


  // Delete badge mutation
  const deleteBadgeMutation = useMutation({
    mutationFn: async (badgeId: string) => {
      const { error } = await supabase
        .from('badges')
        .delete()
        .eq('id', badgeId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['badges'] });
      toast({
        title: "Success",
        description: "Badge deleted successfully",
      });
      setIsDeleting(false);
      setBadgeToDelete('');
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to delete badge",
      });
    },
  });

  const handleDeleteBadge = (badgeId: string) => {
    setBadgeToDelete(badgeId);
    setIsDeleting(true);
  };

  const confirmDelete = () => {
    if (badgeToDelete) {
      deleteBadgeMutation.mutate(badgeToDelete);
    }
  };

  const handleEditBadge = (badge: BadgeType) => {
    // Prevent editing system badges entirely
    if (badge.is_system_badge) {
      toast({
        variant: "destructive",
        title: "Cannot Edit System Badge",
        description: "System badges are protected and cannot be modified.",
      });
      return;
    }
    
    setEditingBadge(badge);
    setIsEditing(true);
  };

  if (badgesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6 sm:space-y-8 animate-fade-in">
        <div className="relative">
          <h1 className="admin-section-title mb-2">Badge Management</h1>
          <p className="text-muted-foreground/80 text-lg">
            Manage badges for highlighting and categorizing businesses
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">All Badges</h2>
            <p className="text-sm text-muted-foreground/80">System and community badges for business categorization</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setIsTranslationManagementOpen(true)}
              className="gap-2 rounded-lg w-full sm:w-auto"
            >
              <Languages className="h-4 w-4" />
              <span className="hidden sm:inline">Manage Translations</span>
              <span className="sm:hidden">Translations</span>
            </Button>
            <Button onClick={() => setIsCreating(true)} className="gap-2 rounded-lg  w-full sm:w-auto">
              <Plus className="h-4 w-4" />
              Create Badge
            </Button>
          </div>
        </div>

      <Card className="admin-card admin-card-stats">
        <CardContent className="p-6">
          <div className="mb-6">
            <p className="text-sm text-muted-foreground/80">
              System badges (with lock icon) are globally managed and completely protected from editing or deletion. 
              Community badges can be created, edited, and removed by administrators.
            </p>
          </div>
          
          {isMobile ? (
            // Mobile card view
            <div className="space-y-4">
              {badges?.map((badge, index) => {
                const IconComponent = getIconComponent(badge.icon || 'Star');
                const isSystem = badge.is_system_badge;
                return (
                  <Card key={badge.id} className={`admin-card admin-card-action animate-fade-in stagger-${Math.min(index + 1, 5)}  ${isSystem ? 'bg-muted/20' : ''}`}>
                    <CardContent className="pt-6">
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <div className="relative flex-shrink-0">
                            <div 
                              className={`flex items-center justify-center w-10 h-10 rounded-full ${
                                isSystem ? 'ring-1 ring-muted-foreground/20' : ''
                              }`}
                              style={{ backgroundColor: badge.color + '20' }}
                            >
                              <IconComponent 
                                className="w-5 h-5"
                                style={{ color: badge.color }}
                              />
                            </div>
                            {isSystem && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-muted-foreground/60 rounded-full flex items-center justify-center">
                                <Lock className="w-2.5 h-2.5 text-background" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
                              <h3 className={`font-medium text-sm text-slate-800 ${isSystem ? 'text-muted-foreground' : ''}`}>
                                {badge.translatedName || badge.name}
                              </h3>
                              {isSystem && (
                                <Badge variant="outline" className="text-xs self-start">
                                  System
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground/80 mb-2">
                              {badge.description}
                            </p>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-4 h-4 rounded-full border"
                                  style={{ backgroundColor: badge.color }}
                                />
                                <span className="text-xs text-muted-foreground/70">Color</span>
                              </div>
                              <StatusBadge isActive={badge.is_active} />
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
                          {badge.is_system_badge ? (
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Lock className="h-4 w-4" />
                              <span className="text-xs">Protected</span>
                            </div>
                          ) : (
                            <>
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleEditBadge(badge)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400"
                                onClick={() => handleDeleteBadge(badge.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            // Desktop table view
            <div className="rounded-xl border border-gray-200/60 overflow-hidden bg-white shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-gray-100 bg-gray-50/50">
                    <TableHead className="font-semibold text-slate-700 h-12">Badge</TableHead>
                    <TableHead className="font-semibold text-slate-700">Color</TableHead>
                    <TableHead className="font-semibold text-slate-700">Status</TableHead>
                    <TableHead className="text-right font-semibold text-slate-700">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {badges?.map((badge) => {
                    const IconComponent = getIconComponent(badge.icon || 'Star');
                    const isSystem = badge.is_system_badge;
                    return (
                      <TableRow 
                        key={badge.id}
                        className={`hover:bg-gray-50/80 border-b border-gray-100/50 transition-colors ${isSystem ? "bg-muted/20" : ""}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <div 
                                className={`flex items-center justify-center w-8 h-8 rounded-full ${
                                  isSystem ? 'ring-1 ring-muted-foreground/20' : ''
                                }`}
                                style={{ backgroundColor: badge.color + '20' }}
                              >
                                <IconComponent 
                                  className="w-4 h-4"
                                  style={{ color: badge.color }}
                                />
                              </div>
                              {isSystem && (
                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-muted-foreground/60 rounded-full flex items-center justify-center">
                                  <Lock className="w-2 h-2 text-background" />
                                </div>
                              )}
                            </div>
                            <div>
                              <div className={`font-medium ${isSystem ? 'text-muted-foreground' : ''}`}>
                                {badge.translatedName || badge.name}
                                {isSystem && (
                                  <Badge variant="outline" className="ml-2 text-xs">
                                    System
                                  </Badge>
                                )}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {badge.description}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div 
                            className="w-6 h-6 rounded-full border"
                            style={{ backgroundColor: badge.color }}
                          />
                        </TableCell>
                        <TableCell>
                          <StatusBadge isActive={badge.is_active} />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {badge.is_system_badge ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center gap-1 text-muted-foreground cursor-help">
                                    <Lock className="h-4 w-4" />
                                    <span className="text-xs">Protected</span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-sm">System badges cannot be edited or deleted</p>
                                  <p className="text-xs text-muted-foreground">They are managed globally across all communities</p>
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      className="h-8 w-8"
                                      onClick={() => handleEditBadge(badge)}
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Edit badge</p>
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      className="h-8 w-8 text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400"
                                      onClick={() => handleDeleteBadge(badge.id)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Delete badge</p>
                                  </TooltipContent>
                                </Tooltip>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleting} onOpenChange={setIsDeleting}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Badge</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this badge? This action cannot be undone.
              All business assignments for this badge will be removed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleting(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteBadgeMutation.isPending}
            >
              {deleteBadgeMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Badge Form Dialogs */}
      <BadgeForm
        isOpen={isCreating}
        onOpenChange={setIsCreating}
        mode="create"
      />
      
      <BadgeForm
        badge={editingBadge}
        isOpen={isEditing}
        onOpenChange={(open) => {
          setIsEditing(open);
          if (!open) setEditingBadge(null);
        }}
        mode="edit"
      />

      {/* Badge Translation Management */}
      <BadgeTranslationManagement
        isOpen={isTranslationManagementOpen}
        onClose={() => setIsTranslationManagementOpen(false)}
      />
      </div>
    </TooltipProvider>
  );
};

export default BadgeManagement;